import { css } from 'lit';

export default css`
  :host {
    --success-color: #0fa;
    --error-color: #e53935;

    --default-color: hsl(from #ccc h s calc(l - 10));
    --pending-color: #bdbdbd;
    --processing-color: #ffe082;
    --received-color: #42a5f5;
    --completed-color: var(--success-color);
    --paid-color: var(--success-color);
    --cancelled-color: var(--error-color);

    --order-background-color: #ffffffb1;
    --order-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
      rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px,
      rgba(0, 0, 0, 0.1) 0px 4px 6px -4px;

    --file-download-block-padding: calc(var(--base-gap) / 2);
    --file-download-base-color: lightcyan;
    --file-download-border-color: hsl(
      from var(--file-download-base-color) h s calc(l - 15)
    );
    --file-download-text-color: hsl(
      from var(--file-download-base-color) h s calc(l - 85)
    );
    --file-download-focus-color: hsl(
      from var(--file-download-base-color) h calc(s + 50) calc(l - 5)
    );
    --file-download-hover-color: hsl(
      from var(--file-download-base-color) h s calc(l - 10)
    );
  }

  #order-container {
    --order-border-color: var(--default-color);

    --tag-background-color: oklch(from var(--order-border-color) 0.96 0.02 h);
    --tag-border-color: oklch(from var(--order-border-color) 0.85 0.08 h);
    --tag-color: oklch(from var(--order-border-color) 0.35 0.12 h);

    display: grid;
    gap: calc(var(--base-gap) * 0.75);
    grid-template-rows: 50px auto minmax(75px, auto);

    border-radius: calc(var(--base-radius) * 0.75);
    padding: var(--base-gap);
    border: 1px solid var(--order-border-color);

    background-color: var(--order-background-color);
    box-shadow: var(--order-shadow);

    transition: height 0.5s ease-in-out;

    &[status='loading'] {
      opacity: 0.6;
      animation: pulse 2s ease-in-out infinite;
    }

    &[status='pending'] {
      --order-border-color: var(--pending-color);
      --tag-background-color: oklch(from var(--pending-color) 0.95 0 30);
      --tag-border-color: var(--pending-color);
      --tag-color: black;
    }

    &[status='received'] {
      --order-border-color: var(--received-color);
    }

    &[status='processing'] {
      --order-border-color: var(--processing-color);
    }

    &[status='completed'] {
      --order-border-color: var(--completed-color);
    }

    &[status='paid'] {
      --order-border-color: var(--paid-color);
    }

    &[status='cancelled'] {
      --order-border-color: var(--cancelled-color);
    }
  }

  #order-status {
    display: flex;
    gap: calc(var(--base-gap) / 2);
    align-items: center;
    line-height: 1.25;

    span {
      align-self: start;
      margin-inline-start: auto;
      border: 1px solid var(--tag-border-color);
      border-radius: var(--base-radius);
      padding-block: calc(var(--base-gap) / 4);
      padding-inline: calc(var(--base-gap) * 0.6);

      background-color: var(--tag-background-color);
      color: var(--tag-color);

      line-height: 1;
    }
  }

  #icon {
    display: flex;
    align-items: center;
  }

  #meta {
    display: flex;
    flex-direction: column;
    justify-content: center;

    h2 {
      margin: 0;
    }
  }

  time {
    font-size: 0.75rem;
  }

  #divider {
    display: block;
    margin-inline: auto;
    width: 90%;
    height: 1px;
    background-color: hsl(from #eee h s calc(l - 5));
  }

  #order-content {
    padding: var(--base-gap);
    border: 1px solid var(--default-color);
    border-radius: var(--base-radius);
    background: rgba(250, 250, 250, 0.68);

    p {
      margin: 0;
    }
  }

  ol {
    li {
      margin-bottom: var(--base-gap);
    }
  }

  table {
    margin-block-end: calc(var(--base-gap) * 0.75);

    caption {
      text-align: start;
      font-weight: bold;
      margin-bottom: calc(var(--base-gap) * 0.5);
    }

    td:nth-child(2) {
      text-align: end;
    }

    width: 100%;
  }

  a[download] {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--base-gap);
    align-items: center;

    border-radius: calc(var(--base-radius) / 2);
    border: 1px solid var(--file-download-border-color);
    width: fit-content;
    padding-block: var(--file-download-block-padding);
    padding-inline: var(--base-gap);

    color: var(--file-download-text-color);
    background-color: var(--file-download-base-color);
    transition: transform 0.2s ease, background-color 0.2s ease;
    line-height: 1;
    text-decoration: none;

    &:hover {
      background-color: var(--file-download-hover-color);
    }

    .download-label {
      display: flex;
      flex-direction: column;
      gap: calc(var(--base-gap) / 4);
      font-weight: 500;

      .download-meta {
        color: lch(from currentColor calc(l + 8) calc(c + 20) calc(h - 10));
        font-weight: normal;
        font-size: 0.7rem;
      }
    }
  }

  .visually-hidden {
    clip-path: inset(100%);
    clip: rect(1px, 1px, 1px, 1px);
    height: 1px;
    width: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
  }
`;
